import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface State {
  error: Error | null;
}

/**
 * A render crash must not leave a blank white page — a blank page is indistinguishable from "still
 * loading" and from "no data", which is the same failure mode this app spends its effort avoiding,
 * just at a different level.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('PlanHub render error', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div className="w-full max-w-md space-y-3 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/50 dark:text-red-100">
          <p className="text-base font-semibold">PlanHub hit an error while rendering</p>
          <p className="font-mono text-xs break-words">{error.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-9 rounded border border-current/40 px-3 font-semibold"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
